<div class="px-4 pt-2 pb-4">
  <!-- Hidden file input -->
  <input
    #fileInput
    type="file"
    multiple
    accept="image/*,application/pdf"
    class="hidden"
    (change)="onFileInputChange($event)"
  />

  <!-- Hidden camera input for iOS Safari (native camera picker) -->
  <input
    #cameraInput
    type="file"
    accept="image/*"
    capture="environment"
    class="hidden"
    (change)="onCameraInputChange($event)"
  />

  <!-- Hidden canvas for camera capture -->
  <canvas #captureCanvas class="hidden"></canvas>

  <div
    class="flex flex-col bg-gray-100 border border-gray-200 rounded-2xl px-4 py-3 transition-all focus-within:ring-2 focus-within:ring-violet-300/30 focus-within:border-violet-300"
  >
    <!-- Textarea row -->
    <textarea
      #textarea
      [(ngModel)]="messageText"
      [placeholder]="placeholder()"
      (input)="onInput()"
      (keydown.enter)="onEnterKey($any($event))"
      class="w-full min-h-[1.5rem] max-h-[120px] border-none bg-transparent resize-none text-base leading-normal text-gray-900 placeholder:text-gray-400 focus:outline-none"
      rows="1"
    ></textarea>

    <!-- Pending attachments preview -->
    <app-input-attachments-preview
      [pendingAttachments]="pendingAttachments()"
      (attachmentRemoveRequested)="requestRemoveAttachment($event)"
    />

    <!-- Bottom actions row -->
    <div class="flex items-center justify-between mt-2 pt-1">
      <!-- Left side actions -->
      <div class="relative flex items-center gap-2">
        <button
          type="button"
          (click)="toggleAttachmentMenu($event)"
          class="flex items-center gap-1.5 text-xs text-gray-600 bg-gray-100 hover:bg-gray-200 px-2.5 py-1 rounded-full border border-gray-200 hover:border-gray-300 transition-colors cursor-pointer"
          [class.bg-gray-200]="isAttachmentMenuOpen"
          [class.border-gray-300]="isAttachmentMenuOpen"
          title="Add attachment"
        >
          <lucide-icon [img]="icons.Plus" class="w-4 h-4"></lucide-icon>
          <span>Add files</span>
        </button>


        <!-- Attachment menu popover -->
        @if (isAttachmentMenuOpen) {
          <div
            class="absolute bottom-full left-0 mb-2 w-56 bg-white rounded-xl shadow-lg border border-gray-200 py-2 z-50"
            (click)="$event.stopPropagation()"
          >
            <button
              type="button"
              (click)="onAddFiles()"
              class="w-full flex items-center gap-3 px-4 py-2.5 text-left text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer"
            >
              <lucide-icon
                [img]="icons.Paperclip"
                class="w-5 h-5 text-gray-500"
              ></lucide-icon>
              <span class="text-sm font-medium">Add files or photos</span>
            </button>
            <button
              type="button"
              (click)="onTakePhoto()"
              class="w-full flex items-center gap-3 px-4 py-2.5 text-left text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer"
            >
              <lucide-icon
                [img]="icons.Camera"
                class="w-5 h-5 text-gray-500"
              ></lucide-icon>
              <span class="text-sm font-medium">Take a photo</span>
            </button>
          </div>
        }
      </div>

      <!-- Right side - send button -->
      <button
        type="button"
        (click)="sendMessage()"
        [disabled]="!canSend"
        class="shrink-0 w-9 h-9 rounded-xl flex items-center justify-center transition-all cursor-pointer disabled:cursor-not-allowed"
        [ngClass]="{
          'bg-violet-400 text-white hover:bg-violet-500 active:scale-95': canSend,
          'bg-gray-300 text-gray-400': !canSend
        }"
        [title]="hasProcessingAttachments ? 'Wait for files to finish uploading' : ''"
      >
        @if (disabled() || hasProcessingAttachments) {
          <lucide-icon
            [img]="icons.Loader2"
            class="w-5 h-5 animate-spin"
          ></lucide-icon>
        } @else {
          <lucide-icon [img]="icons.ArrowUp" class="w-5 h-5"></lucide-icon>
        }
      </button>
    </div>
  </div>

  <!-- Camera modal -->
  @if (isCameraOpen) {
    <div
      class="fixed inset-0 z-50 bg-black/80 flex flex-col items-center justify-center p-4"
      (click)="closeCamera()"
    >
      <div
        class="relative w-full max-w-md bg-black rounded-2xl overflow-hidden"
        (click)="$event.stopPropagation()"
      >
        <!-- Camera preview -->
        <video
          #videoPreview
          autoplay
          playsinline
          muted
          class="w-full aspect-[4/3] object-cover bg-gray-900"
        ></video>

        <!-- Camera error -->
        @if (cameraError) {
          <div
            class="absolute inset-0 flex items-center justify-center bg-gray-900 text-white text-center p-4"
          >
            <div>
              <lucide-icon
                [img]="icons.AlertCircle"
                class="w-10 h-10 mx-auto mb-3 text-red-400"
              ></lucide-icon>
              <p class="text-sm">{{ cameraError }}</p>
            </div>
          </div>
        }

        <!-- Camera controls -->
        <div class="flex items-center justify-center gap-6 py-4 bg-gray-900">
          <button
            type="button"
            (click)="closeCamera()"
            class="w-12 h-12 rounded-full bg-gray-700 text-white flex items-center justify-center hover:bg-gray-600 transition-colors cursor-pointer"
          >
            <lucide-icon [img]="icons.X" class="w-6 h-6"></lucide-icon>
          </button>
          <button
            type="button"
            (click)="capturePhoto()"
            [disabled]="!!cameraError"
            class="w-16 h-16 rounded-full bg-white border-4 border-gray-400 flex items-center justify-center hover:scale-105 transition-transform cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <div class="w-12 h-12 rounded-full bg-white"></div>
          </button>
          <div class="w-12 h-12"></div>
          <!-- Spacer for alignment -->
        </div>
      </div>
    </div>
  }

  <!-- Remove attachment confirmation dialog -->
  <app-confirm-dialog
    #confirmDialog
    title="Remove File"
    [message]="'Are you sure you want to remove ' + (attachmentToRemove?.fileName || 'this file') + '?'"
    confirmText="Remove"
    cancelText="Keep"
    variant="danger"
    (confirmed)="onConfirmRemove()"
    (cancelled)="onCancelRemove()"
  />
</div>
